import { Fragment, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import styled from '@emotion/styled';
import { css, Global, useTheme } from '@emotion/react';
import type {
  ModalProps,
  ModalHeaderProps,
  ModalBodyProps,
  ModalFooterProps,
  FontSizeValue,
} from './Modal.types';
import type { Theme } from '../../theme';

/**
 * Map theme font size token to actual value
 */
const getFontSizeValue = (fontSize: FontSizeValue | undefined, theme: Theme): string | undefined => {
  if (!fontSize) {
    return undefined;
  }
  
  // Check if it's a theme token key
  const themeTokens = ['xs', 'sm', 'base', 'md', 'lg', 'xl'] as const;
  if (themeTokens.includes(fontSize as typeof themeTokens[number])) {
    return theme.typography.fontSize[fontSize as keyof typeof theme.typography.fontSize];
  }
  
  // Otherwise, treat as CSS value
  return typeof fontSize === 'number' ? `${fontSize}px` : String(fontSize);
};

// Transition class names for Headless UI
const transitionClasses = {
  backdropEnter: 'modal-backdrop-enter',
  backdropEnterFrom: 'modal-backdrop-enter-from',
  backdropEnterTo: 'modal-backdrop-enter-to',
  backdropLeave: 'modal-backdrop-leave',
  backdropLeaveFrom: 'modal-backdrop-leave-from',
  backdropLeaveTo: 'modal-backdrop-leave-to',
  modalEnter: 'modal-panel-enter',
  modalEnterFrom: 'modal-panel-enter-from',
  modalEnterTo: 'modal-panel-enter-to',
  modalLeave: 'modal-panel-leave',
  modalLeaveFrom: 'modal-panel-leave-from',
  modalLeaveTo: 'modal-panel-leave-to',
};

// Global styles for transitions (Headless UI uses class names)
const modalTransitionStyles = css`
  /* Backdrop transitions */
  .modal-backdrop-enter {
    transition: opacity 200ms ease-out;
  }
  .modal-backdrop-enter-from {
    opacity: 0;
  }
  .modal-backdrop-enter-to {
    opacity: 1;
  }
  .modal-backdrop-leave {
    transition: opacity 200ms ease-in;
  }
  .modal-backdrop-leave-from {
    opacity: 1;
  }
  .modal-backdrop-leave-to {
    opacity: 0;
  }

  /* Modal panel transitions */
  .modal-panel-enter {
    transition: opacity 200ms ease-out, transform 200ms ease-out;
  }
  .modal-panel-enter-from {
    opacity: 0;
    transform: scale(0.95);
  }
  .modal-panel-enter-to {
    opacity: 1;
    transform: scale(1);
  }
  .modal-panel-leave {
    transition: opacity 150ms ease-in, transform 150ms ease-in;
  }
  .modal-panel-leave-from {
    opacity: 1;
    transform: scale(1);
  }
  .modal-panel-leave-to {
    opacity: 0;
    transform: scale(0.95);
  }
`;

// Styled components
const Backdrop = styled.div<{ $transparent: boolean; $zIndex: number }>`
  position: fixed;
  inset: 0;
  background-color: ${({ $transparent }) =>
    $transparent ? 'transparent' : 'rgba(33, 28, 28, 0.75)'};
  z-index: ${({ $zIndex }) => $zIndex - 10};
`;

const ModalWrapper = styled.div<{ $zIndex: number }>`
  position: fixed;
  inset: 0;
  z-index: ${({ $zIndex }) => $zIndex};
  overflow-y: auto;
`;

const ModalCentering = styled.div`
  display: flex;
  min-height: 100%;
  align-items: center;
  justify-content: center;
  padding: 16px;
  text-align: center;

  @media (max-width: 640px) {
    padding: 8px;
  }
`;

const ModalPanel = styled(Dialog.Panel)<{ 'data-size'?: string }>`
  position: relative;
  width: 100%;
  transform-origin: center;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25);
  text-align: left;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  /* Size variants */
  &[data-size='sm'] {
    max-width: 400px;
    max-height: 80vh;
  }

  &[data-size='md'] {
    max-width: 500px;
    max-height: 85vh;
  }

  &[data-size='lg'] {
    max-width: 600px;
    max-height: 90vh;
  }

  &[data-size='xl'] {
    max-width: 800px;
    max-height: 90vh;
  }

  &[data-size='full'] {
    max-width: 90vw;
    max-height: 90vh;
  }

  /* Mobile responsive */
  @media (max-width: 640px) {
    &[data-size='sm'],
    &[data-size='md'],
    &[data-size='lg'],
    &[data-size='xl'] {
      max-width: 100%;
      max-height: 95vh;
    }

    &[data-size='full'] {
      max-width: 100%;
      max-height: 95vh;
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e5e5;
  flex-shrink: 0;
`;

const Title = styled(Dialog.Title)<{ $fontSize?: string }>`
  margin: 0;
  font-size: ${({ $fontSize }) => $fontSize || '1.25rem'};
  font-weight: 600;
  color: #171717;
  line-height: 1.25;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background-color: transparent;
  color: #525252;
  cursor: pointer;
  transition: background-color 150ms ease-out, color 150ms ease-out;
  flex-shrink: 0;

  &:hover {
    background-color: #f5f5f5;
    color: #171717;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  color: #525252;
  font-size: 0.875rem;
  line-height: 1.5;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f5f5f5;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d4d4d4;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a3a3a3;
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e5e5e5;
  flex-shrink: 0;

  @media (max-width: 640px) {
    flex-direction: column-reverse;
    gap: 8px;

    > * {
      width: 100%;
    }
  }
`;

/**
 * Modal component - Accessible modal dialog using Headless UI
 *
 * @example
 * ```tsx
 * <Modal isOpen={isOpen} onClose={handleClose} title="Confirm Action">
 *   <Modal.Body>
 *     Are you sure you want to proceed?
 *   </Modal.Body>
 *   <Modal.Footer>
 *     <Button variant="secondary" onClick={handleClose}>Cancel</Button>
 *     <Button variant="primary" onClick={handleConfirm}>Confirm</Button>
 *   </Modal.Footer>
 * </Modal>
 * ```
 */
export function Modal({
  isOpen,
  onClose,
  title,
  titleFontSize,
  children,
  size = 'md',
  closeOnBackdropClick = true,
  closeOnEsc = true,
  showCloseButton = true,
  footer,
  className,
  portalContainer,
  'data-testid': testId,
  dataTestId,
  dataId,
  transparent = false,
  zIndex = 50,
  initialFocusRef,
  onMount,
  'aria-label': ariaLabel,
}: ModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onMountCalledRef = useRef(false);
  const theme = useTheme() as Theme;
  
  // Resolve title font size from theme tokens or CSS value
  const resolvedTitleFontSize = getFontSizeValue(titleFontSize, theme);

  // Call onMount when the modal is open and the container is mounted
  useEffect(() => {
    if (isOpen && containerRef.current && onMount && !onMountCalledRef.current) {
      onMountCalledRef.current = true;
      onMount(containerRef.current);
    }
  }, [isOpen, onMount]);

  // Reset the flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      onMountCalledRef.current = false;
    }
  }, [isOpen]);

  const handleBackdropClick = () => {
    if (closeOnBackdropClick) {
      onClose();
    }
  };

  const handleClose = () => {
    if (closeOnEsc) {
      onClose();
    }
  };

  return (
    <>
      <Global styles={modalTransitionStyles} />
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          onClose={handleClose}
          data-testid={dataTestId || testId}
          data-id={dataId}
          initialFocus={initialFocusRef}
          {...(ariaLabel && { 'aria-label': ariaLabel })}
          {...(portalContainer && { container: portalContainer })}
        >
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter={transitionClasses.backdropEnter}
            enterFrom={transitionClasses.backdropEnterFrom}
            enterTo={transitionClasses.backdropEnterTo}
            leave={transitionClasses.backdropLeave}
            leaveFrom={transitionClasses.backdropLeaveFrom}
            leaveTo={transitionClasses.backdropLeaveTo}
          >
            <Backdrop
              onClick={handleBackdropClick}
              aria-hidden="true"
              $transparent={transparent}
              $zIndex={zIndex}
              data-transparent={transparent}
            />
          </Transition.Child>

          {/* Modal container */}
          <ModalWrapper ref={containerRef} $zIndex={zIndex} data-zindex={zIndex}>
            <ModalCentering>
              <Transition.Child
                as={Fragment}
                enter={transitionClasses.modalEnter}
                enterFrom={transitionClasses.modalEnterFrom}
                enterTo={transitionClasses.modalEnterTo}
                leave={transitionClasses.modalLeave}
                leaveFrom={transitionClasses.modalLeaveFrom}
                leaveTo={transitionClasses.modalLeaveTo}
              >
                <ModalPanel
                  className={className}
                  data-size={size}
                  data-testid={testId ? `${testId}-panel` : undefined}
                >
                  {/* Header */}
                  {(title || showCloseButton) && (
                    <Header>
                      {title && (
                        <Title
                          as="h2"
                          $fontSize={resolvedTitleFontSize}
                          data-testid={testId ? `${testId}-title` : undefined}
                        >
                          {title}
                        </Title>
                      )}
                      {showCloseButton && (
                        <CloseButton
                          type="button"
                          onClick={onClose}
                          aria-label="Close modal"
                          data-testid={testId ? `${testId}-close` : undefined}
                        >
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              d="M18 6L6 18M6 6L18 18"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </CloseButton>
                      )}
                    </Header>
                  )}

                  {/* Body */}
                  <Body data-testid={testId ? `${testId}-body` : undefined}>
                    {children}
                  </Body>

                  {/* Footer */}
                  {footer && (
                    <Footer
                      data-testid={testId ? `${testId}-footer` : undefined}
                    >
                      {footer}
                    </Footer>
                  )}
                </ModalPanel>
              </Transition.Child>
            </ModalCentering>
          </ModalWrapper>
        </Dialog>
      </Transition>
    </>
  );
}

/**
 * ModalHeader - Compound component for custom modal header
 */
export function ModalHeader({ children, className }: ModalHeaderProps) {
  return <Header className={className}>{children}</Header>;
}

/**
 * ModalBody - Compound component for modal body content
 */
export function ModalBody({ children, className }: ModalBodyProps) {
  return <Body className={className}>{children}</Body>;
}

/**
 * ModalFooter - Compound component for modal footer actions
 */
export function ModalFooter({ children, className }: ModalFooterProps) {
  return <Footer className={className}>{children}</Footer>;
}

// Attach compound components to Modal
Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

Modal.displayName = 'Modal';
ModalHeader.displayName = 'Modal.Header';
ModalBody.displayName = 'Modal.Body';
ModalFooter.displayName = 'Modal.Footer';

export default Modal;